import Constants from 'js/Constants';
import {
  resetPageNumbers,
  setCurrentPage,
  setSortCriteria,
  setTotalItems,
  setTotalPages,
  updateSearchFilters,
} from 'js/actions';
import SearchBar from 'js/components/MacroInput/SearchBar';
import { formatAll, resultToComplex } from 'js/components/MacroInput/tools/macroTools';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import CardsListPageNumbersContainer from 'js/components/elements/cards/CardsListPageNumbersContainer';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  listSelectStyles,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import UnsortedSelect from 'js/components/elements/rselect/UnsortedSelect';
import { GET_PROJECTS_REQUEST } from 'js/constants/actionTypes';
import { AvailableFilterTypesByView, SearchFilterViews } from 'js/constants/visibilityFilters';
import { MacroOptionsContextWrap } from 'js/contexts/MacroOptionsContext';
import { isAdminAccess, isManagedResource, isOwner, isReadOnly } from 'js/lib/ACLUtils';
import { isDetectedDataset } from 'js/lib/ResourceUtils';
import { areSegmentsDifferent } from 'js/lib/SearchFilterUtils';
import { getEligibleProjectEntitiesList } from 'js/reducers/entities/selectors';
import { pick, toInteger, values } from 'lodash';
import { useDrawerContext } from 'new/components/drawer/useDrawerContext';
import ProjectDetailsActions from 'new/components/pipelines/ProjectsList/ProjectDetailsActions';
import styles from 'new/components/pipelines/ProjectsList/ProjectList.module.scss';
import { getColumns } from 'new/components/pipelines/ProjectsList/columns';
import { ProjectListProps } from 'new/components/pipelines/ProjectsList/types';
import { Table } from 'new/elements/Table';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DetailsIconButton } from 'new/elements/Button';
import { Stack } from '@mui/material';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useHandleAccessLevelChanges } from 'new/hooks/useHandleAccessLevelChanges';
import { useLocalAccessLevel } from 'new/hooks/useLocalAccessLevel';
import { PROJECT_DETAIL_VIEW } from 'js/constants/projects';
import { useProjectAdapter } from 'new/state/projects/adapter/useProjectAdapter';
import { useFlowAdapter } from 'new/state/flows/adapter/useFlowAdapter';
import { UncategorizedFlowsCard } from '../UncategorizedFlowsCard';

export type ProjectsListHandle = {
  loadProjectData: () => void;
};

export const ProjectsList = forwardRef<
  ProjectsListHandle,
  ProjectListProps & { setProjectDetail?: any; setTab?: any }
>(function ProjectsList(
  {
    selectedProject,
    parentScrollRef,
    setProjectDetail,
    hideActions,
    inModal,
    listing,
    onItemClick,
    onListingChange,
  },
  ref,
) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { requestProjects } = useProjectAdapter();
  const { requestFlowsWithoutProjectsCount } = useFlowAdapter();

  const isLoading = useIsLoading(GET_PROJECTS_REQUEST);
  const usersByID = useSelector((state: any) => state.entities.users);
  const { drawerContentRef } = useDrawerContext();
  const projects = useSelector(getEligibleProjectEntitiesList);
  const { currentPage, sortCriteria } = useSelector((state: any) => state.app.listing);
  const currentSortCriteria = inModal ? listing.sortCriteria : sortCriteria;
  const { filters: allFilters, availableFilters: allAvailableFilters } = useSelector(
    (state: any) => state.app.visibilityFilters,
  );
  const isMountEffect = useRef(true);
  const prevFilters = useRef();
  const filters = allFilters[SearchFilterViews.PROJECTS];
  const availableFilterTypes = AvailableFilterTypesByView[SearchFilterViews.PROJECTS];
  const urlParams = new URLSearchParams(history.location.search);
  const pageParam = toInteger(urlParams.get('page'));
  const searchParam = urlParams.get('search');

  const onDetailClicked = (data, type) => {
    setProjectDetail({ ...data, type });
  };

  const columns = useMemo(() => getColumns({ usersByID, onDetailClicked }), [usersByID]);

  const rowEndRender = (row) => {
    if (hideActions) {
      return null;
    }

    const readOnly = isReadOnly(row);
    const isManaged = isManagedResource(row);
    const isDetected = isDetectedDataset(row);

    const isAdmin = isOwner(row) || isAdminAccess(row);

    return (
      <Stack direction="row" alignItems="center" gap={1}>
        {isAdmin ? (
          <Stack marginBottom={0.5}>
            <DetailsIconButton
              onClick={(e) => {
                e.stopPropagation();
                onDetailClicked(row, PROJECT_DETAIL_VIEW);
              }}
            />
          </Stack>
        ) : null}
        <ProjectDetailsActions
          data={row}
          readOnly={readOnly}
          onDetailClicked={onDetailClicked}
          isManaged={isManaged}
          isDetected={isDetected}
        />
      </Stack>
    );
  };

  const handleRowClick = useCallback(
    (row) => {
      if (onItemClick) {
        onItemClick(row);
      } else {
        history.push(`/integrate/projects/${row.id}`);
      }
    },
    [onItemClick],
  );

  const handleSearchSuggestionSelected = (projectId) => {
    history.push(`/integrate/projects/${projectId}`);
  };

  const sortCriteriaOptions = values(Constants.SortCriteriaInfo);

  const selectedSortOption = getCurrentSelectedOption(sortCriteriaOptions, currentSortCriteria);

  const onSortCriteriaChanged = async (selectedOption) => {
    if (inModal) {
      onListingChange({
        type: 'updateSortCriteria',
        value: selectedOption.value,
      });
    } else {
      dispatch(setSortCriteria(getNewSelectionValue(selectedOption, true, '')));
    }
  };

  const { aclQueryMode, ...aclProps } = useLocalAccessLevel();

  const loadProjectData = useCallback(() => {
    const availableFilters = pick(allAvailableFilters, availableFilterTypes);
    /**
     * if inModal, use the access level locally
     */
    const options = inModal ? { aclQueryMode } : {};

    requestProjects(currentPage, currentSortCriteria, availableFilters, options).then(
      (response: { totalPages: any; totalItems: any }) => {
        if (!inModal) {
          dispatch(setTotalPages(response.totalPages));
          dispatch(setTotalItems(response.totalItems));
        }
      },
    );
  }, [
    allAvailableFilters,
    availableFilterTypes,
    inModal,
    aclQueryMode,
    currentPage,
    currentSortCriteria,
    dispatch,
    requestProjects,
  ]);

  useEffect(() => {
    requestFlowsWithoutProjectsCount();
  }, [requestFlowsWithoutProjectsCount]);

  useImperativeHandle(ref, () => ({ loadProjectData }), [loadProjectData]);
  useHandleAccessLevelChanges(loadProjectData, [aclQueryMode]); // registering the listening hook for access level changes
  useHandleAccessLevelChanges(requestFlowsWithoutProjectsCount, [aclQueryMode]); // registering the listening hook for access level changes

  useEffect(() => {
    if (inModal) return;

    dispatch(resetPageNumbers());

    if (searchParam) {
      dispatch(
        updateSearchFilters(
          SearchFilterViews.PROJECTS,
          resultToComplex(formatAll(searchParam)).segments,
        ),
      );
    }

    if (pageParam) {
      dispatch(setCurrentPage(pageParam));
    }
  }, []);

  useEffect(() => {
    // don't load the list data during the first mount if search/pagination
    // query params are set. wait for Redux state updates in the mount effect to
    // trigger this effect.
    if (!inModal && !(isMountEffect.current && pageParam && pageParam !== 1)) {
      loadProjectData();
    }

    isMountEffect.current = false;
  }, [currentPage, currentSortCriteria]);

  useEffect(() => {
    if (filters && prevFilters.current && areSegmentsDifferent(filters, prevFilters.current)) {
      loadProjectData();
    }

    prevFilters.current = filters;
  }, [filters]);

  return (
    <>
      <div className={styles.controlsContainer}>
        <MacroOptionsContextWrap>
          <SearchBar
            view={SearchFilterViews.PROJECTS}
            availableFilterTypes={AvailableFilterTypesByView[SearchFilterViews.PROJECTS]}
            onResourceSelected={handleSearchSuggestionSelected}
            inModal={inModal}
            accessLevelControlProps={{ controlled: inModal, ...aclProps }}
          />
        </MacroOptionsContextWrap>
        <UnsortedSelect
          options={sortCriteriaOptions}
          components={{ Input: SelectInput, Option: SelectOption }}
          isClearable={false}
          isSearchable={true}
          backspaceRemovesValue={false}
          value={selectedSortOption}
          onChange={onSortCriteriaChanged}
          styles={listSelectStyles}
        />
        <CardsListPageNumbersContainer
          isLoading={isLoading}
          onListingChange={onListingChange}
          listing={listing}
          inModal={inModal}
        />
      </div>

      {inModal ? null : (
        <Stack padding={2}>
          <UncategorizedFlowsCard />
        </Stack>
      )}

      {!isLoading && (!projects || !projects.length) && (
        <NoContentTextView title={<div>Looks empty here. Try creating a new project.</div>} />
      )}

      <Table
        isSkeletonLoadingMode
        loading={isLoading}
        getRowClassName={(row) =>
          selectedProject?.id === row?.id ? styles.selectedRow : undefined
        }
        parentScrollRef={parentScrollRef || drawerContentRef}
        showCheckbox={false}
        columns={columns}
        rows={projects}
        rowEndRender={rowEndRender}
        height={1200}
        onRowClick={handleRowClick}
      />
    </>
  );
});
